import { apiClient, handleApiError } from './api.js';

class PatientService {
  // Get all patients
  async getAllPatients() {
    try {
      return await apiClient.get('/patient');
    } catch (error) {
      console.error('Error getting all patients:', error);
      throw error;
    }
  }

  // Get patients for dropdown (minimal data)
  async getPatientsForDropdown() {
    try {
      return await apiClient.get('/patient/dropdown');
    } catch (error) {
      console.error('Error getting patients for dropdown:', error);
      throw error;
    }
  }

  // Get active patients
  async getActivePatients() {
    try {
      return await apiClient.get('/patient/active');
    } catch (error) {
      console.error('Error getting active patients:', error);
      throw error;
    }
  }

  // Get patient by ID
  async getPatientById(patientId) {
    try {
      return await apiClient.get(`/patient/${patientId}`);
    } catch (error) {
      console.error(`Error getting patient ${patientId}:`, error);
      throw error;
    }
  }

  // Search patients
  async searchPatients(searchTerm) {
    try {
      return await apiClient.get(`/patient/search?searchTerm=${searchTerm}`);
    } catch (error) {
      console.error('Error searching patients:', error);
      throw error;
    }
  }

  // Create a new patient
  async createPatient(patient) {
    try {
      // Ensure all required fields are included
      const patientData = {
        ...patient,
        // Add default values for required fields if not provided
        postalCode: patient.postalCode || '',
        country: patient.country || '',
        countryId: patient.countryId || 0,
        state: patient.state || '',
        stateId: patient.stateId || 0,
        city: patient.city || '',
        cityId: patient.cityId || 0,
        emergencyContactName: patient.emergencyContactName || patient.emergencyContact || '',
        emergencyContactPhone: patient.emergencyContactPhone || '',
        insuranceProvider: patient.insuranceProvider || '',
        insurancePolicyNumber: patient.insurancePolicyNumber || '',
        // Initialize empty collections for required navigation properties
        medicalRecords: patient.medicalRecords || [],
        appointments: patient.appointments || [],
        invoices: patient.invoices || [],
        // Initialize navigation properties for user tracking
        // These will be populated by the backend based on the JWT token
        // Providing empty User objects with UserId property to satisfy model validation
        createdByUser: { userId: 0 },
        updatedByUser: { userId: 0 }
      };
      
      return await apiClient.post('/patient', patientData);
    } catch (error) {
      console.error('Error creating patient:', error);
      throw error;
    }
  }

  // Update an existing patient
  async updatePatient(patientId, patient) {
    try {
      // The backend will handle timestamp tracking and user tracking via the JWT token
      // UpdatedBy and UpdatedAt are extracted from the JWT token in the backend
      const patientData = {
        ...patient,
        // Initialize navigation property for user tracking
        // This will be populated by the backend based on the JWT token
        // Providing empty User object with UserId property to satisfy model validation
        updatedByUser: { userId: 0 }
      };
      
      return await apiClient.put(`/patient/${patientId}`, patientData);
    } catch (error) {
      console.error(`Error updating patient ${patientId}:`, error);
      throw error;
    }
  }

  // Delete a patient
  async deletePatient(patientId) {
    try {
      return await apiClient.delete(`/patient/${patientId}`);
    } catch (error) {
      console.error(`Error deleting patient ${patientId}:`, error);
      throw error;
    }
  }

  // Activate or deactivate a patient
  async activatePatient(patientId, isActive) {
    try {
      return await apiClient.put(`/patient/${patientId}/activate`, { isActive });
    } catch (error) {
      console.error(`Error ${isActive ? 'activating' : 'deactivating'} patient ${patientId}:`, error);
      throw error;
    }
  }

  // Get patient statistics
  async getPatientStatistics(startDate, endDate) {
    try {
      return await apiClient.get(`/patient/statistics?startDate=${startDate}&endDate=${endDate}`);
    } catch (error) {
      console.error('Error getting patient statistics:', error);
      throw error;
    }
  }

  // Get patient medical records - this should use the MedicalRecordsService instead
  async getPatientMedicalRecords(patientId) {
    try {
      return await apiClient.get(`/medicalrecord/patient/${patientId}`);
    } catch (error) {
      console.error(`Error getting medical records for patient ${patientId}:`, error);
      throw error;
    }
  }

  // Get patient appointments - this should use the AppointmentService instead
  async getPatientAppointments(patientId) {
    try {
      return await apiClient.get(`/appointment/patient/${patientId}`);
    } catch (error) {
      console.error(`Error getting appointments for patient ${patientId}:`, error);
      throw error;
    }
  }

  // Get patient billing records - this should use the BillingService instead
  async getPatientBillingRecords(patientId) {
    try {
      return await apiClient.get(`/billing/invoices/patient/${patientId}`);
    } catch (error) {
      console.error(`Error getting billing records for patient ${patientId}:`, error);
      throw error;
    }
  }
}

export default new PatientService();